import Error from 'next/error';
import React from 'react';
import styled from 'styled-components';
import { Box, Column, Row } from '../../../components/Flex';
import MerchantNav from '../../../components/merchant/MerchantNav';
import useMerchant, { decodeMerchant } from '../../../hooks/useMerchant';
import useSWR from 'swr';
import { NextPageContext } from 'next';
import cookies from 'next-cookies';
import AddItem from '../../../components/merchant/AddItemModal';
import DeleteItem from '../../../components/merchant/DeleteitemModal';
import UpdateItem from '../../../components/merchant/UpdateItemModal';
import { useRouter } from 'next/router';
import { WHITE_LABEL_ACCENT_COLOR } from '../../../lib/white-label';
import Link from 'next/link';

const Container = styled(Row)`
  width: 100%;
  margin-right: auto;
  margin-left: auto;
  @media only screen and (max-width: 800px) {
    flex-direction: column;
  }
`;

const Card = styled.div`
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 5px 36px #919db529;
  border: 2px solid #e1eaf3;
  border-radius: 15px;
  width: 100%;
  padding: 30px;
`;

export const ManageLink = styled.a`
  color: ${WHITE_LABEL_ACCENT_COLOR};

  &:hover {
    opacity: 0.75;
  }
`;

const MerchantItems = ({ fallbackData, cookie }) => {
  const merchant = useMerchant(decodeMerchant(cookie));
  const router = useRouter();
  const { data: items, mutate } = useSWR('/market/item', { fallbackData });
  if (merchant.role !== 'merchant') return <Error statusCode={404} />;

  const onItemChange = (e) => {
    mutate(items);
  };

  return (
    <Container>
      <Column>
        <MerchantNav role={merchant.role} route="items" />
        <Box height={20} />
        <Card>
          <div className="flex flex-col">
            <div className="overflow-x-auto">
              <div className="p-1.5 w-full inline-block align-middle">
                <div className="flex w-full justify-between">
                  <h1 className="font-bold">Merchant Items</h1>
                  <AddItem onClose={onItemChange} />
                </div>

                <div className="overflow-hidden border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase "
                        >
                          Item
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase "
                        >
                          Inventory Count
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase "
                        >
                          Category
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-xs font-bold text-right text-gray-500 uppercase "
                        >
                          Edit
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-xs font-bold text-right text-gray-500 uppercase "
                        >
                          Delete
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {items.map((item) => (
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <img
                                  className="h-10 w-10 rounded-full"
                                  src={item.imageUrl}
                                  alt=""
                                />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 flex flex-row">
                                  <div>{item.name}</div>
                                  <div className="pl-2">
                                    <Link
                                      legacyBehavior
                                      href={`/shop/category/${item?.category?.slug}/item/${item.slug}`}
                                      target="_blank"
                                    >
                                      <ManageLink>
                                        <svg
                                          fill="none"
                                          stroke="currentColor"
                                          stroke-width="1.5"
                                          viewBox="0 0 24 24"
                                          xmlns="http://www.w3.org/2000/svg"
                                          aria-hidden="true"
                                          className='class="w-4 h-4 text-blue'
                                        >
                                          <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                                          ></path>
                                        </svg>
                                      </ManageLink>
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-800 whitespace-nowrap">
                            <div className="flex flex-row">
                              <span className="pr-1">
                                {item?.inventory?.length}
                              </span>
                              <ManageLink
                                className="pr-1"
                                onClick={() => {
                                  router.push(
                                    `/shop/merchant/item/${item.id}/inventory`,
                                  );
                                }}
                              >
                                (Manage)
                              </ManageLink>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-800 whitespace-nowrap">
                            {item.category.name}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                            <UpdateItem values={item} onClose={onItemChange} />
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                            <DeleteItem
                              itemId={item.id}
                              onClose={onItemChange}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Column>
    </Container>
  );
};

export async function getServerSideProps(ctx: NextPageContext) {
  const cookie = cookies(ctx).accessToken;

  const items = await fetch(`${process.env.SHOP_BACKEND_URI}/market/item`, {
    headers: {
      authorization: `Bearer ${cookie}`,
    },
  }).then((res) => {
    return res.json();
  });

  return {
    props: {
      fallbackData: items,
      cookie,
    },
  };
}

export default MerchantItems;
